import React, { useEffect, useState } from 'react';
import api from '../api/client';

/**
 * Configurable fallbacks. These are used when the matching admin Setting is
 * empty, so the pieces work after a plain web deploy even before the server
 * side ships. Edit here, or set them in Admin → Settings once deployed.
 */
export const WHATSAPP_NUMBER = ''; // e.g. '919812345678' — a WhatsApp-capable MOBILE (wa.me won't dial a landline)
export const WHATSAPP_GREETING = "Hi Narayan Home Facility Services, I'd like to know more about your services.";

// wa.me wants a full international number, digits only, no '+'.
function toWaNumber(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.length === 10) d = '91' + d; // bare Indian mobile → add country code
  return d;
}

/**
 * Floating WhatsApp button — fixed bottom-right on every site page. Opens a
 * WhatsApp chat with the business. Renders only when a real number is set, so
 * it never becomes a dead link.
 */
export function WhatsAppFab({ number, greeting }) {
  const wa = toWaNumber(number || WHATSAPP_NUMBER);
  if (!wa) return null; // no number configured → don't show a broken button
  const href = `https://wa.me/${wa}?text=${encodeURIComponent(greeting || WHATSAPP_GREETING)}`;

  return (
    <a className="wa-fab" href={href} target="_blank" rel="noreferrer"
       aria-label="Chat with us on WhatsApp">
      <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true">
        <path fill="#fff" d="M16.003 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.6 4.46 1.73 6.4L3.2 28.8l6.57-1.72a12.74 12.74 0 0 0 6.23 1.6h.01c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.33-6.63-3.75-9.05A12.7 12.7 0 0 0 16.003 3.2zm0 23.3h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-4 1.05 1.07-3.9-.25-.4a10.56 10.56 0 0 1-1.62-5.64c0-5.86 4.77-10.63 10.64-10.63a10.6 10.6 0 0 1 10.62 10.64c0 5.86-4.77 10.62-10.63 10.62zm5.83-7.96c-.32-.16-1.9-.94-2.19-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1.01 1.25-.19.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.58-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.15-.15.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.74-.99-2.38-.26-.62-.53-.54-.72-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66 0 1.57 1.14 3.08 1.3 3.29.16.21 2.25 3.43 5.44 4.81.76.33 1.35.52 1.81.67.76.24 1.46.21 2.01.13.61-.09 1.9-.78 2.17-1.53.27-.75.27-1.39.19-1.53-.08-.13-.29-.21-.61-.37z"/>
      </svg>
      <span className="wa-fab__label">Chat on WhatsApp</span>
    </a>
  );
}

/**
 * "Registered & Certified" logo band, placed under the hero. Compliance badges
 * are fixed files in /public/registrations. Any missing file hides itself, and
 * the whole band hides if none load — so it's safe to ship before the images
 * are added.
 */
const REGISTRATIONS = [
  { src: '/registrations/skill-india.png', alt: 'Skill India — Kaushal Bharat' },
  { src: '/registrations/labour-dept-haryana.png', alt: 'Labour Department, Haryana' },
  { src: '/registrations/certificate-of-incorporation.png', alt: 'Certificate of Incorporation' },
  { src: '/registrations/msme-udyam.png', alt: 'MSME · Udyam Registration' },
  { src: '/registrations/iso-9001.png', alt: 'ISO 9001:2015 Certified' },
  { src: '/registrations/gst.png', alt: 'GST Registered' },
];

export function RegistrationsBand() {
  // Primary source is admin-managed (uploaded in Admin → Registrations). If the
  // API returns nothing, fall back to any static files in /public/registrations.
  const [items, setItems] = useState(null); // null = loading

  useEffect(() => {
    let alive = true;
    api.get('/registrations')
      .then((d) => {
        if (!alive) return;
        const list = (d?.registrations || []).map((r) => ({ src: r.image, alt: r.label || 'Registration', link: r.link }));
        setItems(list.length ? list : REGISTRATIONS);
      })
      .catch(() => { if (alive) setItems(REGISTRATIONS); });
    return () => { alive = false; };
  }, []);

  // Track which images actually load, so missing files (and the whole band)
  // hide themselves — never show a row of broken-image icons.
  const [ok, setOk] = useState({});
  const source = items || [];
  const anyLoaded = source.length > 0 && source.some((_, i) => ok[i] !== false);

  if (items === null) return null; // don't flash an empty band while loading

  const Img = ({ r, i }) => (
    <img
      src={r.src}
      alt={r.alt}
      title={r.alt}
      loading="lazy"
      hidden={ok[i] === false}
      onError={() => setOk((p) => ({ ...p, [i]: false }))}
    />
  );

  return (
    <section className="reg-band" hidden={!anyLoaded} aria-label="Registrations and certifications">
      <div className="wrap">
        <p className="reg-band__title">Registered &amp; Certified</p>
        <div className="reg-band__row">
          {source.map((r, i) => (
            r.link
              ? <a key={i} href={r.link} target="_blank" rel="noreferrer" hidden={ok[i] === false}><Img r={r} i={i} /></a>
              : <Img key={i} r={r} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
